import { useCallback, useEffect, useMemo, useRef, type RefObject } from 'react';

type WelcomeMotion = {
    seekTo: (value: number, behavior?: ScrollBehavior) => void;
    next: () => void;
};

type Chapter = {
    label: string;
    next: string;
    seek?: number;
    footer?: boolean;
};

const chapters: readonly Chapter[] = [
    {
        label: 'Início',
        next: 'Explore a Servion',
        seek: 0.36,
    },
    {
        label: 'Emissão e organização',
        next: 'Conheça a automação',
        seek: 0.64,
    },
    {
        label: 'Emissão recorrente',
        next: 'Veja a automação',
        seek: 1,
    },
    {
        label: 'Telas da Servion',
        next: 'Mais informações',
        footer: true,
    },
];

const motionProperties = [
    '--srv-welcome-ambient-presence',
    '--srv-welcome-automation-presence',
    '--srv-welcome-header-background',
    '--srv-welcome-presentation-text',
    '--srv-welcome-presentation-muted',
    '--srv-welcome-presentation-accent',
    '--srv-welcome-presentation-line',
    '--srv-welcome-presentation-surface',
] as const;

const clamp = (value: number, min = 0, max = 1) =>
    Math.min(max, Math.max(min, value));

const smooth = (start: number, end: number, value: number) => {
    const amount = clamp((value - start) / (end - start));
    return amount * amount * (3 - 2 * amount);
};

const mixColor = (from: number[], to: number[], amount: number) =>
    `rgb(${from
        .map((channel, index) =>
            Math.round(channel + (to[index] - channel) * amount),
        )
        .join(' ')})`;

/** Scroll-driven motion stays outside React's render cycle and inside this page. */
export function useWelcomeMotion(
    rootRef: RefObject<HTMLDivElement | null>,
): WelcomeMotion {
    const controllerRef = useRef<WelcomeMotion | null>(null);

    const seekTo = useCallback((value: number, behavior?: ScrollBehavior) => {
        controllerRef.current?.seekTo(value, behavior);
    }, []);

    const next = useCallback(() => {
        controllerRef.current?.next();
    }, []);

    useEffect(() => {
        const currentRoot = rootRef.current;
        if (!currentRoot) return;
        const root = currentRoot;

        const rail = root.querySelector<HTMLElement>('.experience-rail');
        const stage = root.querySelector<HTMLElement>('.experience-stage');
        if (!rail || !stage) return;

        const heroBackdrop = root.querySelector<HTMLElement>('.hero-backdrop');
        const presentationNavigation =
            root.querySelector<HTMLElement>('.experience-footer');
        const siteFooter = root.querySelector<HTMLElement>('.site-footer');
        const scenes = Array.from(
            root.querySelectorAll<HTMLElement>('[data-scene]'),
        );
        const chapterDescription = root.querySelector<HTMLElement>(
            '#chapter-description',
        );
        const nextButton = root.querySelector<HTMLElement>('#next-chapter');
        const film = root.querySelector<HTMLElement>('#ambient-film');
        const video = root.querySelector<HTMLVideoElement>('#ambient-video');
        const poster = root.querySelector<HTMLElement>(
            '#ambient-poster, .ambient-poster',
        );
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
        const restore: (() => void)[] = [];
        let disposed = false;
        let frame: number | null = null;
        let currentScene = -1;
        let videoFailed = Boolean(video?.error);
        let videoReady = false;
        let videoRecovering = false;
        let recoveryAttempted = false;
        let videoRequest: AbortController | null = null;
        let videoObjectUrl: string | null = null;
        let pendingSeekTime: number | null = null;
        const originalVideoSrc = video?.getAttribute('src') ?? null;

        function preserveStyle(
            element: HTMLElement | null,
            properties: readonly string[],
        ) {
            if (!element) return;
            const previous = properties.map((property) => ({
                property,
                value: element.style.getPropertyValue(property),
                priority: element.style.getPropertyPriority(property),
            }));
            restore.push(() => {
                for (const { property, value, priority } of previous) {
                    if (value) {
                        element.style.setProperty(property, value, priority);
                    } else {
                        element.style.removeProperty(property);
                    }
                }
            });
        }

        function preserveAttribute(element: HTMLElement, name: string) {
            const previous = element.getAttribute(name);
            restore.push(() => {
                if (previous === null) element.removeAttribute(name);
                else element.setAttribute(name, previous);
            });
        }

        function preserveTextNode(element: HTMLElement | null) {
            if (!element) return null;
            const existing = Array.from(element.childNodes).find(
                (node): node is Text => node.nodeType === Node.TEXT_NODE,
            );
            if (existing) {
                const previous = existing.nodeValue;
                restore.push(() => {
                    existing.nodeValue = previous;
                });
                return existing;
            }

            const fallback = document.createTextNode('');
            element.insertBefore(fallback, element.firstChild);
            restore.push(() => fallback.remove());
            return fallback;
        }

        preserveStyle(root, motionProperties);
        for (const attribute of [
            'data-progress',
            'data-active-scene',
            'data-video-time',
            'data-video-ready',
            'data-ambient',
        ]) {
            preserveAttribute(root, attribute);
        }
        preserveStyle(heroBackdrop, ['opacity', 'visibility']);
        preserveStyle(presentationNavigation, ['opacity']);
        preserveStyle(film, ['opacity', 'transform']);
        for (const element of [video, poster]) {
            preserveStyle(element, ['width', 'left', 'top', 'transform']);
        }
        preserveStyle(video, ['opacity']);

        for (const element of scenes) {
            preserveStyle(element, ['opacity', 'transform']);
            preserveAttribute(element, 'aria-hidden');
            const wasActive = element.classList.contains('is-active');
            const wasInert = element.inert;
            restore.push(() => {
                element.classList.toggle('is-active', wasActive);
                element.inert = wasInert;
            });
        }
        if (presentationNavigation) {
            preserveAttribute(presentationNavigation, 'aria-hidden');
            const wasInert = presentationNavigation.inert;
            restore.push(() => {
                presentationNavigation.inert = wasInert;
            });
        }
        const chapterDescriptionText = preserveTextNode(chapterDescription);
        const nextButtonText = preserveTextNode(nextButton);

        const originalVideoOpacity = video?.style.opacity ?? '';

        function presentationDistance() {
            return Math.max(1, rail!.offsetHeight - stage!.offsetHeight);
        }

        function railTop() {
            return rail!.getBoundingClientRect().top + window.scrollY;
        }

        function progress() {
            return clamp((window.scrollY - railTop()) / presentationDistance());
        }

        function updateAmbientState() {
            root.dataset.ambient = videoReady ? 'video' : 'poster';
            root.dataset.videoReady = String(videoReady);
            if (video) {
                video.style.opacity = videoReady ? originalVideoOpacity : '0';
            }
        }

        function hasSeekableTimeline() {
            if (!video) return false;
            for (let index = 0; index < video.seekable.length; index++) {
                if (video.seekable.end(index) > 1 / 24) return true;
            }
            return false;
        }

        function showVideoFallback() {
            videoFailed = true;
            videoReady = false;
            videoRecovering = false;
            pendingSeekTime = null;
            updateAmbientState();
            schedule();
        }

        async function recoverVideoTimeline() {
            if (disposed || !video || recoveryAttempted) return;
            recoveryAttempted = true;
            videoRecovering = true;
            videoReady = false;
            pendingSeekTime = null;
            updateAmbientState();
            schedule();

            const request = new AbortController();
            videoRequest = request;
            try {
                const source = new URL(
                    originalVideoSrc ?? video.currentSrc,
                    window.location.href,
                );
                if (
                    source.origin !== window.location.origin ||
                    !['http:', 'https:'].includes(source.protocol)
                ) {
                    showVideoFallback();
                    return;
                }
                const requestOptions: RequestInit = {
                    signal: request.signal,
                    cache: 'force-cache',
                    credentials: 'same-origin',
                };
                // eslint-disable-next-line no-restricted-globals -- Static MP4 bytes; apiFetch requires JSON and adds API/XSRF headers.
                const response = await fetch(source.href, requestOptions);
                if (!response.ok) throw new Error('Video asset unavailable');
                const bytes = await response.blob();
                if (disposed || request.signal.aborted) return;
                if (!bytes.size) throw new Error('Video asset is empty');

                // A Blob has a complete seekable timeline even when the host
                // serves this static file without HTTP Range support.
                videoObjectUrl = URL.createObjectURL(bytes);
                video.src = videoObjectUrl;
                video.load();
            } catch {
                if (!disposed && !request.signal.aborted) showVideoFallback();
            } finally {
                if (videoRequest === request) videoRequest = null;
            }
        }

        function checkVideoTimeline() {
            if (disposed || !video || videoFailed || video.readyState < 2)
                return;
            if (hasSeekableTimeline()) {
                // The original source may become seekable while its recovery
                // download is pending; retain the normal source in that case.
                if (videoRecovering && !videoObjectUrl) videoRequest?.abort();
                videoRecovering = false;
                videoReady = true;
                updateAmbientState();
                schedule();
            } else if (videoObjectUrl) {
                showVideoFallback();
            } else if (!videoRecovering) {
                void recoverVideoTimeline();
            }
        }

        function updateAmbient(
            value: number,
            footerProgress: number,
            exitOffset: number,
        ) {
            const routine = smooth(0.19, 0.59, value);
            const product = smooth(0.63, 0.97, value);
            const automation =
                smooth(0.49, 0.58, value) * (1 - smooth(0.69, 0.78, value));
            const mobile = window.innerWidth < 760;
            const baseWidth = mobile
                ? 0.98 + routine * 0.04 + product * 0.25
                : 0.5 + routine * 0.07 + product * 0.24;
            const baseX = mobile
                ? 0.84 + product * 0.1
                : 0.8 + routine * 0.01 + product * 0.12;
            const baseY = mobile
                ? 0.47 - product * 0.06
                : 0.48 - routine * 0.02 - product * 0.04;
            const markWidth = mobile
                ? baseWidth
                : baseWidth + (0.43 - baseWidth) * automation;
            const centerX = mobile
                ? baseX
                : baseX + (0.28 - baseX) * automation;
            const centerY = mobile
                ? baseY
                : baseY + (0.41 - baseY) * automation;
            const angle =
                -6 + routine * 4 + product * 6 - (mobile ? 0 : 4 * automation);

            if (film) {
                film.style.opacity = (
                    smooth(0.12, 0.3, value) *
                    (1 - clamp(footerProgress))
                ).toFixed(4);
                film.style.transform = `translate3d(0, ${-exitOffset}px, 0)`;
            }
            for (const element of [video, poster]) {
                if (!element) continue;
                element.style.width = `${(window.innerWidth * markWidth) / 0.4}px`;
                element.style.left = `${window.innerWidth * centerX}px`;
                element.style.top = `${window.innerHeight * centerY}px`;
                element.style.transform = `translate(-50%, -50%) rotate(${angle}deg)`;
            }

            if (video && videoReady && !videoRecovering && !videoFailed) {
                const duration = Number.isFinite(video.duration)
                    ? Math.max(0, video.duration - 1 / 24)
                    : 0;
                const targetTime = reduced.matches
                    ? 0
                    : smooth(0.19, 0.93, value) * duration;
                if (
                    duration &&
                    !video.seeking &&
                    Math.abs(video.currentTime - targetTime) > 1 / 48
                ) {
                    try {
                        pendingSeekTime = targetTime;
                        video.currentTime = targetTime;
                    } catch {
                        pendingSeekTime = null;
                        if (!recoveryAttempted) void recoverVideoTimeline();
                        else showVideoFallback();
                    }
                }
            }
            root.dataset.videoTime = (video?.currentTime ?? 0).toFixed(4);
        }

        function update() {
            frame = null;
            if (disposed) return;

            const value = progress();
            const presence = 1 - smooth(0.12, 0.32, value);
            const paper = 1 - presence;
            const isDark = document.documentElement.classList.contains('dark');
            if (heroBackdrop) {
                heroBackdrop.style.opacity = presence.toFixed(4);
                heroBackdrop.style.visibility =
                    presence < 0.001 ? 'hidden' : 'visible';
            }
            const styles = root.style;
            styles.setProperty(
                '--srv-welcome-ambient-presence',
                paper.toFixed(4),
            );
            styles.setProperty(
                '--srv-welcome-automation-presence',
                (
                    smooth(0.49, 0.58, value) *
                    (1 - smooth(0.69, 0.78, value))
                ).toFixed(4),
            );
            styles.setProperty(
                '--srv-welcome-header-background',
                isDark
                    ? `rgba(16,39,45,${(0.72 + 0.12 * paper).toFixed(3)})`
                    : `rgba(217,223,217,${(0.62 + 0.22 * paper).toFixed(3)})`,
            );
            styles.setProperty(
                '--srv-welcome-presentation-text',
                mixColor(
                    [255, 255, 255],
                    isDark ? [240, 240, 233] : [38, 63, 69],
                    paper,
                ),
            );
            styles.setProperty(
                '--srv-welcome-presentation-muted',
                mixColor(
                    [224, 236, 228],
                    isDark ? [170, 195, 189] : [97, 119, 122],
                    paper,
                ),
            );
            styles.setProperty(
                '--srv-welcome-presentation-accent',
                mixColor(
                    [185, 232, 212],
                    isDark ? [126, 220, 177] : [68, 119, 111],
                    paper,
                ),
            );
            styles.setProperty(
                '--srv-welcome-presentation-line',
                mixColor(
                    isDark ? [63, 104, 90] : [188, 212, 199],
                    isDark ? [48, 78, 75] : [212, 222, 216],
                    paper,
                ),
            );
            styles.setProperty(
                '--srv-welcome-presentation-surface',
                isDark
                    ? `rgba(16,39,45,${(0.8 * paper).toFixed(3)})`
                    : `rgba(245,244,239,${(0.8 * paper).toFixed(3)})`,
            );

            const exitOffset = Math.max(
                0,
                window.scrollY - railTop() - presentationDistance(),
            );
            const footerProgress = clamp(
                exitOffset / Math.max(1, siteFooter?.offsetHeight || 1),
            );
            const scene =
                value < 0.24 ? 0 : value < 0.5 ? 1 : value < 0.79 ? 2 : 3;
            const opacity = [
                1 - smooth(0.13, 0.23, value),
                smooth(0.23, 0.32, value) * (1 - smooth(0.41, 0.49, value)),
                smooth(0.49, 0.58, value) * (1 - smooth(0.69, 0.78, value)),
                smooth(0.78, 0.89, value),
            ];
            const offsets = [
                -smooth(0.15, 0.24, value) * 24,
                (1 - smooth(0.21, 0.33, value)) * 28 -
                    smooth(0.4, 0.5, value) * 20,
                (1 - smooth(0.47, 0.59, value)) * 28 -
                    smooth(0.69, 0.8, value) * 20,
                (1 - smooth(0.76, 0.9, value)) * 28,
            ];
            scenes.forEach((element, index) => {
                element.style.opacity = opacity[index].toFixed(4);
                element.style.transform = `translateY(${reduced.matches ? 0 : offsets[index]}px)`;
                element.classList.toggle('is-active', index === scene);
                element.setAttribute(
                    'aria-hidden',
                    index === scene ? 'false' : 'true',
                );
                element.inert = index !== scene;
            });
            if (scene !== currentScene) {
                currentScene = scene;
                if (chapterDescriptionText) {
                    chapterDescriptionText.nodeValue = chapters[scene].label;
                }
                if (nextButtonText) {
                    nextButtonText.nodeValue = `${chapters[scene].next} `;
                }
            }
            if (presentationNavigation) {
                presentationNavigation.style.opacity = (
                    1 - smooth(0.03, 0.52, footerProgress)
                ).toFixed(3);
                presentationNavigation.inert = footerProgress > 0.52;
                presentationNavigation.setAttribute(
                    'aria-hidden',
                    String(footerProgress > 0.52),
                );
            }
            root.dataset.progress = value.toFixed(4);
            root.dataset.activeScene = String(scene);
            updateAmbient(value, footerProgress, exitOffset);
        }

        function schedule() {
            if (disposed || frame !== null) return;
            frame = window.requestAnimationFrame(update);
        }

        const controller: WelcomeMotion = {
            seekTo(value, behavior = 'instant') {
                const destination =
                    railTop() +
                    clamp(Number(value) || 0) * presentationDistance();
                window.scrollTo({
                    top: destination,
                    behavior: reduced.matches ? 'instant' : behavior,
                });
                schedule();
            },
            next() {
                if (chapters[currentScene]?.footer && siteFooter) {
                    siteFooter
                        .querySelector<HTMLElement>('.footer-wordmark')
                        ?.focus({ preventScroll: true });
                    window.scrollTo({
                        top:
                            document.documentElement.scrollHeight -
                            window.innerHeight,
                        behavior: reduced.matches ? 'instant' : 'smooth',
                    });
                    schedule();
                } else {
                    controller.seekTo(
                        chapters[currentScene]?.seek ?? 0.36,
                        'smooth',
                    );
                }
            },
        };
        controllerRef.current = controller;

        function onLoadedData() {
            checkVideoTimeline();
        }

        function onVideoSeeked() {
            if (disposed || !video) return;
            const requestedTime = pendingSeekTime;
            pendingSeekTime = null;
            if (
                requestedTime !== null &&
                Math.abs(video.currentTime - requestedTime) > 1 / 48
            ) {
                if (!recoveryAttempted) void recoverVideoTimeline();
                else showVideoFallback();
            }
            schedule();
        }

        function onVideoError() {
            if (!disposed) showVideoFallback();
        }

        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule);
        window.addEventListener('pageshow', schedule);
        reduced.addEventListener('change', schedule);
        video?.addEventListener('loadeddata', onLoadedData);
        video?.addEventListener('progress', checkVideoTimeline);
        video?.addEventListener('seeked', onVideoSeeked);
        video?.addEventListener('error', onVideoError);

        const observer =
            typeof ResizeObserver === 'undefined'
                ? null
                : new ResizeObserver(schedule);
        for (const element of [rail, stage, siteFooter]) {
            if (element) observer?.observe(element);
        }
        const themeObserver = new MutationObserver(schedule);
        themeObserver.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class'],
        });
        void document.fonts?.ready.then(schedule);
        updateAmbientState();
        update();
        checkVideoTimeline();

        return () => {
            disposed = true;
            if (frame !== null) window.cancelAnimationFrame(frame);
            window.removeEventListener('scroll', schedule);
            window.removeEventListener('resize', schedule);
            window.removeEventListener('pageshow', schedule);
            reduced.removeEventListener('change', schedule);
            video?.removeEventListener('loadeddata', onLoadedData);
            video?.removeEventListener('progress', checkVideoTimeline);
            video?.removeEventListener('seeked', onVideoSeeked);
            video?.removeEventListener('error', onVideoError);
            videoRequest?.abort();
            if (videoObjectUrl) {
                if (video?.getAttribute('src') === videoObjectUrl) {
                    if (originalVideoSrc === null) video.removeAttribute('src');
                    else video.setAttribute('src', originalVideoSrc);
                    if (video.isConnected) video.load();
                }
                URL.revokeObjectURL(videoObjectUrl);
            }
            observer?.disconnect();
            themeObserver.disconnect();
            if (controllerRef.current === controller) {
                controllerRef.current = null;
            }
            for (const restoreValue of restore.reverse()) restoreValue();
        };
    }, [rootRef]);

    return useMemo(() => ({ seekTo, next }), [seekTo, next]);
}
